import React, {FC, useRef, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateLicenseTypesEnum} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ColorDot} from '../../../../../libs/shared/ui/src/components/utility/colorDot';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CollapsedItemStates} from '../../../../helpers/expansionTogglingHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {VisualWorkspaceTeamData} from '../../../../models/api/visualWorkspaceModel';
import {
  WorkspaceDropReorderTeamInboxHandler,
  WorkspaceDropReorderTeamViewHandler,
} from '../../../../store/data/workspace/reorder/types';
import {
  TagModelMap,
  TeamInboxModelMap,
} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {NavigationLineIcon} from '../../../common/navigation/navigationLineIcon';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {NavigationLineStandardIcon} from '../../../common/navigation/navigationLineStandardIcon';
import {WorkspacesEditorInboxesDropdownContainer} from '../dropdowns/workspacesEditorInboxesDropdownContainer';
import {WorkspacesEditorPickTeamViewsDropdownContainer} from '../dropdowns/workspacesEditorPickTeamViewsDropdownContainer';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {WorkspaceLineDropTarget} from '../line/workspaceLineDropTarget';
import {WorkspaceLineMore} from '../line/workspaceLineMore';
import {Workspace} from '../workspace';
import {
  ToggleDropdownTeamCustomViewHandler,
  ToggleDropdownTeamInboxHandler,
  ToggleExpandItemHandler,
  ToggleTeamCustomViewHandler,
  ToggleTeamInboxHandler,
} from './context/types';
import {StyledEditModeLabelDiv} from './teammateWorkspaceEditor';
import {TeamWorkspaceEditorCustomViewsContainer} from './teamWorkspaceEditorCustomViewsContainer';
import {TeamWorkspaceEditorInboxesContainer} from './teamWorkspaceEditorInboxesContainer';

/*
 * Props.
 */

export interface TeamWorkspaceEditorProps {
  panelId: InboxesPanelId;
  /** User license type (full). */
  userLicenseType: TeammateLicenseTypesEnum;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Hash map of workspace item expansion toggle states. */
  collapsedItems: CollapsedItemStates;
  /** Handler for toggling the expanded/collapsed state of section items. */
  onToggleExpandSection: ToggleExpandItemHandler;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpandClick: () => void;
  /** Event handler that manages a team inbox's checked state in P1. */
  onToggleTeamInbox: ToggleTeamInboxHandler;
  /** Event handler that manages a team inbox's checked state in the search modal. */
  onToggleDropdownTeamInbox: ToggleDropdownTeamInboxHandler;
  /** Event handler that manages a team custom view's checked state. */
  onToggleTeamCustomView: ToggleTeamCustomViewHandler;
  onToggleDropdownTeamCustomView: ToggleDropdownTeamCustomViewHandler;
  /** Current team. */
  team: TeamModel;
  /** Whether the VisualWorkspaceModel snapshot is alphabetized. */
  isAlphabetized: boolean;
  /** Team data from VisualWorkspaceModel snapshot. */
  teamData: VisualWorkspaceTeamData;
  /** Map that stores toggled team inboxes to display in P1. */
  teamInboxModelMap: TeamInboxModelMap;
  /** Map that stores toggled team custom views to display in P1. */
  teamCustomViewModelMap: TagModelMap;
  onDropReorderTeamInbox: WorkspaceDropReorderTeamInboxHandler;
  onDropReorderTeamView: WorkspaceDropReorderTeamViewHandler;
  hasCustomViews: boolean;
}

/*
 * Component.
 */

/** Display a workspace for the specified team. */
export const TeamWorkspaceEditor: FC<TeamWorkspaceEditorProps> = (props) => {
  const {panelId, isExpanded, userLicenseType, team, hasCustomViews} = props;

  const [shouldShowTeamInboxesDropdown, setShouldShowTeamInboxesDropdown] = useState<boolean>(false);
  const [shouldShowTeamCustomViewsDropdown, setShouldShowTeamCustomViewsDropdown] = useState<boolean>(false);

  const onTeamInboxesClick = () => setShouldShowTeamInboxesDropdown(true);
  const onTeamInboxesDismiss = () => setShouldShowTeamInboxesDropdown(false);
  const addTeamInboxesRef = useRef<HTMLDivElement>(null);

  const onTeamCustomViewsClick = () => setShouldShowTeamCustomViewsDropdown(true);
  const onTeamCustomViewsDismiss = () => {
    setShouldShowTeamCustomViewsDropdown(false);
    setCustomViewsDropdownSelectedIds([]);
  };
  const onToggleDropdownTeamCustomView = (customView: TagModel, teamId: number) => {
    props.onToggleDropdownTeamCustomView(customView, teamId);
    setCustomViewsDropdownSelectedIds([...new Set(customViewsDropdownSelectedIds).add(customView.id)]);
  };
  const [customViewsDropdownSelectedIds, setCustomViewsDropdownSelectedIds] = useState<Array<number>>([]);

  const addTeamCustomViewsRef = useRef<HTMLDivElement>(null);

  if (userLicenseType !== TeammateLicenseTypesEnum.FULL) {
    return null;
  }

  /*
   * Event handlers.
   */

  const onToggleExpandClick = () => {
    props.onToggleExpandClick();
  };

  /*
   * Render.
   */

  return (
    <Workspace isExpanded={isExpanded}>
      {/* Header. */}
      <WorkspaceHeader
        panelId={panelId}
        interactionId="team"
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpandClick}
        isEditingWorkspace
      >
        {team.name}
      </WorkspaceHeader>
      {/* Workspace content. */}
      {isExpanded &&
        renderWorkspace({
          props,
          addTeamInboxesRef,
          addTeamCustomViewsRef,
          onTeamInboxesClick,
          onTeamInboxesDismiss,
          shouldShowTeamInboxesDropdown,
          onTeamCustomViewsClick,
          onTeamCustomViewsDismiss,
          shouldShowTeamCustomViewsDropdown,
          onToggleDropdownTeamCustomView,
          customViewsDropdownSelectedIds,
          hasCustomViews,
        })}
    </Workspace>
  );
};

/*
 * Helpers.
 */

interface RenderWorkspaceArgs {
  props: TeamWorkspaceEditorProps;
  onTeamInboxesClick: () => void;
  onTeamInboxesDismiss: () => void;
  shouldShowTeamInboxesDropdown: boolean;
  onTeamCustomViewsClick: () => void;
  onTeamCustomViewsDismiss: () => void;
  shouldShowTeamCustomViewsDropdown: boolean;
  addTeamInboxesRef: React.RefObject<HTMLDivElement>;
  addTeamCustomViewsRef: React.RefObject<HTMLDivElement>;
  onToggleDropdownTeamCustomView: ToggleDropdownTeamCustomViewHandler;
  customViewsDropdownSelectedIds: Array<number>;
  hasCustomViews: boolean;
}

function renderWorkspace(args: RenderWorkspaceArgs) {
  const {
    props,
    addTeamInboxesRef,
    addTeamCustomViewsRef,
    onTeamInboxesClick,
    onTeamInboxesDismiss,
    shouldShowTeamInboxesDropdown,
    onTeamCustomViewsClick,
    onTeamCustomViewsDismiss,
    shouldShowTeamCustomViewsDropdown,
    onToggleDropdownTeamCustomView,
    customViewsDropdownSelectedIds,
    hasCustomViews,
  } = args;
  const {panelId, team, teamData, isAlphabetized, onToggleDropdownTeamInbox} = props;

  return (
    <>
      {/* Inboxes. */}
      <TeamWorkspaceEditorInboxesContainer
        team={team}
        teamData={teamData}
        onToggle={props.onToggleTeamInbox}
        panelId={panelId}
        // TODO: setting isAlphabetized should not be necessary. The child components (header items) using this prop use it for reordering but aren't actually reorderable.
        isAlphabetized={isAlphabetized}
        collapsedItems={props.collapsedItems}
        onToggleExpandSection={props.onToggleExpandSection}
        teamInboxModelMap={props.teamInboxModelMap}
        onDropReorderTeamInbox={props.onDropReorderTeamInbox}
      />
      {/* Custom Views. */}
      {hasCustomViews && (
        <TeamWorkspaceEditorCustomViewsContainer
          team={team}
          teamData={teamData}
          onToggle={props.onToggleTeamCustomView}
          panelId={panelId}
          teamCustomViewModelMap={props.teamCustomViewModelMap}
          onDropReorderTeamView={props.onDropReorderTeamView}
        />
      )}
      {/* CTA for adding team inboxes. */}
      {renderAddTeamInboxesButton(onTeamInboxesClick, addTeamInboxesRef, shouldShowTeamInboxesDropdown)}
      {hasCustomViews &&
        renderAddTeamCustomViewsButton(
          onTeamCustomViewsClick,
          addTeamCustomViewsRef,
          shouldShowTeamCustomViewsDropdown,
        )}
      {/* Dropdowns */}
      {maybeRenderAddTeamInboxesDropdown({
        anchor: addTeamInboxesRef,
        team,
        shouldShowTeamItemDropdown: shouldShowTeamInboxesDropdown,
        onDismiss: onTeamInboxesDismiss,
        onToggle: onToggleDropdownTeamInbox,
        visibleItemsIds: teamData.inboxes.map((selectedInbox) => selectedInbox.id),
      })}
      {maybeRenderAddTeamCustomViewsDropdown({
        anchor: addTeamCustomViewsRef,
        team,
        shouldShowTeamItemDropdown: shouldShowTeamCustomViewsDropdown,
        onDismiss: onTeamCustomViewsDismiss,
        onToggle: onToggleDropdownTeamCustomView,
        visibleItemsIds: teamData.customViews.map((selectedCustomView) => selectedCustomView.id),
        customViewsDropdownSelectedIds,
      })}
    </>
  );
}

function renderAddTeamInboxesButton(
  onAddTeamInboxesClick: () => void,
  addTeamInboxesRef: React.RefObject<HTMLDivElement>,
  shouldShowTeamInboxesDropdown: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <div ref={addTeamInboxesRef}>
          <WorkspaceLineDropTarget
            indentLevel={0}
            onClick={onAddTeamInboxesClick}
            shouldAlphabetizeWorkspace={false}
            workspaceDragItem={undefined}
            isSelected={shouldShowTeamInboxesDropdown}
          >
            <NavigationLineIcon>
              <ColorDot size={8} color={theme.palette.blue.shade40} />
            </NavigationLineIcon>
            <NavigationLineLabel>
              <StyledEditModeLabelDiv>
                <Trans id="edit-mode-add-team-inboxes">Add shared inboxes</Trans>
              </StyledEditModeLabelDiv>
            </NavigationLineLabel>
            <WorkspaceLineMore isEditingWorkspaceButton />
          </WorkspaceLineDropTarget>
        </div>
      )}
    </UserThemeConsumer>
  );
}

function renderAddTeamCustomViewsButton(
  onAddTeamCustomViewsClick: () => void,
  addTeamCustomViewsRef: React.RefObject<HTMLDivElement>,
  shouldShowTeamCustomViewsDropdown: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <div ref={addTeamCustomViewsRef}>
          <WorkspaceLineDropTarget
            indentLevel={0}
            onClick={onAddTeamCustomViewsClick}
            shouldAlphabetizeWorkspace={false}
            workspaceDragItem={undefined}
            isSelected={shouldShowTeamCustomViewsDropdown}
          >
            <NavigationLineStandardIcon iconName="customViewsSmall" color={theme.palette.blue.shade40} />
            <NavigationLineLabel>
              <StyledEditModeLabelDiv>
                <Trans id="edit-mode-add-custom-views">Add custom views</Trans>
              </StyledEditModeLabelDiv>
            </NavigationLineLabel>
            <WorkspaceLineMore isEditingWorkspaceButton />
          </WorkspaceLineDropTarget>
        </div>
      )}
    </UserThemeConsumer>
  );
}

interface AddTeamItemArgs {
  team: TeamModel;
  anchor: React.RefObject<HTMLElement>;
  shouldShowTeamItemDropdown: boolean;
  onDismiss: () => void;
  visibleItemsIds: Array<number>;
}

interface AddTeamInboxesArgs extends AddTeamItemArgs {
  onToggle: ToggleDropdownTeamInboxHandler;
}

interface AddTeamCustomViewsArgs extends AddTeamItemArgs {
  onToggle: ToggleDropdownTeamCustomViewHandler;
  customViewsDropdownSelectedIds: Array<number>;
}

function maybeRenderAddTeamInboxesDropdown(args: AddTeamInboxesArgs) {
  const {anchor, shouldShowTeamItemDropdown, onDismiss, team, onToggle, visibleItemsIds} = args;
  return (
    shouldShowTeamItemDropdown && (
      <WorkspacesEditorInboxesDropdownContainer
        team={team}
        anchor={anchor}
        placement="right-start"
        onRequestClose={onDismiss}
        onToggleTeamInbox={onToggle}
        visibleInboxesIds={visibleItemsIds}
      />
    )
  );
}

function maybeRenderAddTeamCustomViewsDropdown(args: AddTeamCustomViewsArgs) {
  const {
    team,
    anchor,
    shouldShowTeamItemDropdown,
    onDismiss,
    onToggle,
    visibleItemsIds,
    customViewsDropdownSelectedIds,
  } = args;
  return (
    shouldShowTeamItemDropdown && (
      <WorkspacesEditorPickTeamViewsDropdownContainer
        team={team}
        anchor={anchor}
        placement="right-start"
        titleLabel={<Trans id="workspace-editor-teamcustomviews">{team.name} views</Trans>}
        onRequestClose={onDismiss}
        onToggleTeamCustomView={onToggle}
        visibleTeamCustomViewIds={visibleItemsIds}
        tagDropdownSelectedIds={customViewsDropdownSelectedIds}
      />
    )
  );
}
